import { AppData, Lightning, Registry, Router } from '@lightningjs/sdk';
import Page from 'components/Page';
import { Episode, LiveEventStream, Video, Show } from 'types/api/media';
import { PageId } from 'types/pageId';
import { SeekType, ViewContext } from 'types/analytics';
import { RoutePath } from 'config/routes';
import {
  debounce,
  getFocusDepth,
  debounceAnnounce,
} from 'support/generalUtils';
import { constants } from 'aliases';
import { translate } from 'support/translate';
import { MILLISECONDS_IN_SECOND } from 'support/timeUtils';
import VideoOverlay from 'components/pages/playback/overlay/VideoOverlay';
import { ErrorCause } from 'components/widgets/ErrorModal';
import { ListItemContext } from 'types/events';
import BrightcovePlayer from 'components/pages/playback/player-components/BrightcovePlayer';
import VodPlayerEventHandler from 'components/pages/playback/PlayerEventHandler/VodPlayerEventHandler';
import LivePlayerEventHandler from 'components/pages/playback/PlayerEventHandler/LivePlayerEventHandler';
import LightningPlayer from 'components/pages/playback/player-components/LightningPlayer';
import AbstractPlayer from 'components/pages/playback/player-components/AbstractPlayer';
import TextTrackDisplay from 'components/pages/playback/TextTrackDisplay';
import { navigateDetailsFromVideo } from 'support/routerUtils';
import { InteractiveAdData } from '../interactive-ads/InteractiveAdUtils';
import { AdCuePoint } from '../ad-managers/AbstractAdManager';
import { isCCEnabled } from 'support/playerUtils';
import {
  reportClosedCaptionState,
  reportStartOver,
} from 'services/analytics/reportingServiceVideo';

const INTERACTIVE_TO_IDLE_TRANSITION_TIME = 5000;

const FULL_SCREEN_PLAYER_X = 0;
const FULL_SCREEN_PLAYER_Y = 0;
const FULL_SCREEN_PLAYER_WIDTH = 1920;
const FULL_SCREEN_PLAYER_HEIGHT = 1080;

export interface AbstractPlaybackPageTemplateSpec
  extends Lightning.Component.TemplateSpec {
  pageData:
    | {
        mediaContent: Video | LiveEventStream;
        hasDrm?: boolean;
        drmLicenseUrl?: string;
        token?: string;
        progress?: number;
        upNext?: (Episode | Show)[];
        viewContext: ViewContext;
        listItemContext: ListItemContext;
        fromPageId: PageId;
      }
    | undefined;
  Content: {
    LightningPlayer: typeof LightningPlayer;
    BrightcovePlayer: typeof BrightcovePlayer;
    PlayerEventHandler:
      | typeof VodPlayerEventHandler
      | typeof LivePlayerEventHandler;
    VideoOverlay: typeof VideoOverlay;
    TextTrackDisplay: typeof TextTrackDisplay;
  };
}

export default abstract class AbstractPlaybackPage
  extends Page<AbstractPlaybackPageTemplateSpec>
  implements
    Lightning.Component.ImplementTemplateSpec<AbstractPlaybackPageTemplateSpec>
{
  protected override _pageId = PageId.PLAYBACK;

  protected _buffering = false;
  protected _hasReachedEnd = false;
  protected _paused = false;
  protected _isCurrentlySeeking = false;
  protected _ccEnabled = false;
  protected _isAdPlaying = false;
  protected _shouldAnnounceCurrentTime = false;
  protected _idleCheckTimeoutId: number | null = null;
  protected _shouldResumeOnForeground = false;

  protected _video: Video | null = null;

  protected _ayswIdleLimit = 0;
  protected _ayswTimeoutFunction: (() => void) | null = null;
  protected _ayswLiveEventEndedTimeout = null;
  protected _ayswTimer = null;
  protected _shouldPromptAysw = false;

  protected _Player: AbstractPlayer = undefined!;
  protected _Content = this.getByRef('Content')!;
  protected _LightningPlayer: LightningPlayer =
    this._Content.getByRef('LightningPlayer')!;
  protected _BrightcovePlayer: BrightcovePlayer =
    this._Content.getByRef('BrightcovePlayer')!;
  protected _PlayerEventHandler = this._Content.getByRef('PlayerEventHandler')!;
  protected _VideoOverlay = this._Content.getByRef('VideoOverlay')!;
  protected _TextTrackDisplay = this._Content.getByRef('TextTrackDisplay')!;

  private _debugPlayPause = this._handleMediaPlayPause.bind(this);
  private _debugRewind = this._handleMediaRewind.bind(this);
  private _debugFastForward = this._handleMediaFastForward.bind(this);

  protected _restartIdleTimer = debounce(
    this.idleCheck.bind(this),
    INTERACTIVE_TO_IDLE_TRANSITION_TIME,
  );

  protected static PlayerEventHandlerType:
    | typeof VodPlayerEventHandler
    | typeof LivePlayerEventHandler = VodPlayerEventHandler;

  static override _template(): Lightning.Component.Template<AbstractPlaybackPageTemplateSpec> {
    return {
      ...super._template(),
      Content: {
        LightningPlayer: {
          visible: false,
          collision: true,
          type: LightningPlayer,
          signals: {
            interactiveAdInit: '$interactiveAdInit',
            cuePointsChanged: '$cuePointsChanged',
          },
        },
        BrightcovePlayer: {
          visible: false,
          collision: true,
          type: BrightcovePlayer,
          signals: {
            cuePointsChanged: '$cuePointsChanged',
          },
        },
        PlayerEventHandler: {
          type: this.PlayerEventHandlerType,
          signals: {
            $setIsAdPlaying: '$handleAdStateChange',
            $videoPlayerEnded: '$videoPlayerEnded',
            $videoPlayerPlay: '$videoPlayerPlay',
            $videoPlayerPause: '$videoPlayerPause',
            $videoPlayerSeeked: '$videoPlayerSeeked',
            $videoPlayerSeeking: '$videoPlayerSeeking',
            $videoPlayerTimeUpdate: '$videoPlayerTimeUpdate',
            $videoPlayerUnloading: '$videoPlayerUnloading',
            $fatalError: '$fatalError',
            $bufferStart: '$bufferStart',
            $bufferEnd: '$bufferEnd',
            $streamLoaded: '$streamLoaded',
          },
        },
        TextTrackDisplay: {
          type: TextTrackDisplay,
        },
        VideoOverlay: {
          type: VideoOverlay,
          signals: {
            skip: '_skip',
            seek: '_seek',
            pause: '_pause',
            seekPause: '_seekPause',
            seekEnd: '_seekEnd',
            play: '_play',
            seeking: '_seeking',
            startOver: '_startOver',
            ccAudio: '_ccAudio',
            onSubtitlesChange: '_onSubtitlesChange',
            onAudioChange: '_onAudioChange',
            openMenu: '_openMenu',
            closeMenu: '_closeMenu',
            liveButton: '_liveButton',
            onControlHover: '_onControlHover',
          },
        },
      },
    };
  }

  override _onDataProvided() {
    if (!this.pageData) {
      Router.navigate(RoutePath.ERROR);
      return;
    }

    const { mediaContent, fromPageId, listItemContext, viewContext } =
      this.pageData;

    // Close current player if one is currently available
    if (this._Player) this._Player.close();

    const useBc = AppData?.device.canUseBrightcoveSdk();
    this._LightningPlayer.patch({ visible: !useBc });
    this._BrightcovePlayer.patch({ visible: useBc });
    this._Player = useBc ? this._BrightcovePlayer : this._LightningPlayer;

    if (!this.setupPlayer(!!useBc)) return;

    this._VideoOverlay.initialize();
    this._VideoOverlay.duration = Number(mediaContent.durationSecs);
    this._VideoOverlay.mediaContent = mediaContent;

    this._setState('Idle');
    // TODO: handle setting correct player
    this._PlayerEventHandler.patch({
      playerType: this._Player.playerType,
    });
    this._PlayerEventHandler.initialize(
      this._Player,
      mediaContent,
      fromPageId,
      viewContext,
      listItemContext,
    );
    this._initPlayback();

    reportClosedCaptionState(this._ccEnabled);
  }

  override _enable() {
    this._Player.patch({
      absoluteX: FULL_SCREEN_PLAYER_X,
      absoluteY: FULL_SCREEN_PLAYER_Y,
      w: FULL_SCREEN_PLAYER_WIDTH,
      h: FULL_SCREEN_PLAYER_HEIGHT,
    });
    super._enable();
  }

  override _setup() {
    super._setup();
    this._setState('Idle');
    this._ccEnabled = isCCEnabled();
  }

  override _active() {
    this.hideBackground();

    window.backgroundingService.addBackgroundingAction(
      'playbackBackgrounded',
      this._onBackgrounded.bind(this),
    );
    window.backgroundingService.addForegroundingAction(
      'playbackForegrounded',
      this._onForegrounded.bind(this),
    );
    this.setupAyswTimer();
    this.setupAysw();
    this._handleCcChange = this.handleCcChange;

    AppData!.notificationService.addBlockingElement();
  }

  override _inactive() {
    this._LightningPlayer.patch({ visible: false });
    this._BrightcovePlayer.patch({ visible: false });

    window.backgroundingService.removeBackgroundingAction(
      'playbackBackgrounded',
    );
    window.backgroundingService.removeForegroundingAction(
      'playbackForegrounded',
    );

    this.cleanupPlayer();
    this.showBackground();
    this._setState('Idle');
    this.clearIdleCheckTimeout();
    this.clearAyswTimeout();
    this._TextTrackDisplay._disable();
    // Ensure the overlay can be shown on return visits since Lightning holds onto the PlaybackPage components
    this._VideoOverlay.enableOverlay();

    this._handleCcChange = undefined;
    this._Player = undefined!;

    AppData!.notificationService.removeBlockingElement();
  }

  /* =============================
   * Key input handling
   * ============================= */

  override _captureKey(event: KeyboardEvent) {
    this.resetAyswTimer();

    return false;
  }

  override _handleMediaPlay() {
    this._play();
    this._setState('Interactive');
    this._restartIdleTimer();
    return true;
  }

  override _handleMediaPause() {
    this._pause();
    this._setState('Interactive');
    this._restartIdleTimer();
    return true;
  }

  override _handleMediaPlayPause() {
    if (!this._Player) return;

    if (this._Player.isPlaying()) {
      this._handleMediaPause();
    } else {
      this._handleMediaPlay();
    }
  }

  override _handleMediaStop() {
    this.exitPlayer();
  }

  override _handleMediaFastForward() {
    if (!this._Player) return;

    this._setState('Interactive');
    this._VideoOverlay.handleMediaFFRW('FF');
  }

  override _handleMediaRewind() {
    if (!this._Player) return;

    this._setState('Interactive');
    this._VideoOverlay.handleMediaFFRW('RW');
  }

  override _handleEnter() {
    this._setState('Interactive');
  }

  _handleClick() {
    this._setState('Interactive');
  }

  /* =============================
   * Player event handling
   * ============================= */

  $handleAdStateChange(isAdPlaying: boolean) {
    // no-op
  }

  $videoPlayerEnded() {
    this._hasReachedEnd = true;
  }

  $videoPlayerPlay() {
    this._paused = false;
    this._VideoOverlay.updateFromPlay();
    this.updateBufferingState();
  }

  $videoPlayerPause() {
    this._paused = true;
    this._VideoOverlay.updateFromPause();
    this.updateBufferingState();
  }

  $videoPlayerSeeked() {
    this._isCurrentlySeeking = false;
    this.updateBufferingState();
  }

  $videoPlayerSeeking(rawTime: number, relativeTime: number) {
    this._isCurrentlySeeking = true;
    this.updateBufferingState();
  }

  $videoPlayerTimeUpdate(relativeTime: number, hasReachedCredits: boolean) {
    // no-op
  }

  $videoPlayerUnloading() {
    // During trueX, the player gets unloaded and won't fire any events.
    // Reset this flag manually
    this._isCurrentlySeeking = false;
    this.$bufferEnd();
  }

  $fatalError() {
    // Do not handle error if page is already inactive
    if (!this.active) return;

    // End playback on fatal error
    this.cleanupPlayer();

    this.widgets.errormodal.update({
      cause: ErrorCause.PLAYBACK,
    });

    AppData?.modalService.open('ErrorModal', { replaceCurrent: true });
  }

  $bufferStart() {
    this._buffering = true;
    this.updateBufferingState();
  }

  $bufferEnd() {
    this._buffering = false;
    this.updateBufferingState();
  }

  $streamLoaded() {
    if (AppData?.device.useTextTrackComponent()) {
      this.setUpTextTrackDisplay();
    }
  }

  /* =============================
   * Player Ad handling
   * ============================= */

  $interactiveAdInit(data: InteractiveAdData) {
    // VOD specific
  }

  $cuePointsChanged(cuePoints: AdCuePoint[] | null) {
    // VOD specific
  }

  /* =============================
   * Video overlay handling
   * ============================= */

  _skip(increment: number) {
    let seekType: SeekType | null = null;

    if (increment > 0) {
      seekType = SeekType.STEP_FORWARD;
    } else {
      seekType = SeekType.STEP_BACKWARD;
    }

    this._PlayerEventHandler.patch({ seekType });

    // We should make sure targetSeek doesn't go below 0,
    // eg. when spamming SkipBackward during playback.
    const targetSeek = Math.max(
      0,
      this._Player.currentContentTime() + increment,
    );
    this._seek(targetSeek);
  }

  _seek(position: number) {
    // no-op
  }

  _pause() {
    this._Player.pause();
    debounceAnnounce(this, translate('ttsPrompts.paused'));
  }

  _seekPause() {
    this._Player.seekPause();
  }

  _seekEnd() {
    // no-op
  }

  _play() {
    this._Player.resume();
    this._shouldAnnounceCurrentTime = true;
  }

  _seeking() {
    this._restartIdleTimer();
  }

  _startOver() {
    this._PlayerEventHandler.patch({ seekType: SeekType.START_OVER });
    this._seek(0);
    reportStartOver();
  }

  _ccAudio() {
    const currSubtitle = AppData!.storageService.subtitle.get();
    const currAudio = AppData!.storageService.audio.get();
    const subtitles = this._Player.getSubtitles();

    let ccIndex = -1;
    let audioIndex = -1;

    if (subtitles.length) {
      if (currSubtitle) ccIndex = this._Player.getCcIndex(currSubtitle);
    }

    const audio = this._Player.getAudioOptions();
    if (currAudio) {
      audioIndex = audio.findIndex(audioOption => audioOption === currAudio);
    }
    // If no audio index selected but we have audio options, use the first one (Default)
    if (audioIndex === -1 && audio.length) {
      audioIndex = 0;
    }

    const cc = [constants.closedCaption.none, ...subtitles];

    if (subtitles.length && currSubtitle) {
      ccIndex = this._Player.getCcIndex(currSubtitle) + 1; // +1 to account for the none subtitle
    } else {
      ccIndex = 0;
    }

    this._VideoOverlay.updateCc(ccIndex, audioIndex, cc, audio);
  }

  _onSubtitlesChange(option: string) {
    this._ccEnabled = option !== constants.closedCaption.none;
    if (!this._ccEnabled) {
      this._Player.disableCc();
      this._TextTrackDisplay.reset();
    } else {
      this._Player.enableCc(option);
    }

    AppData!.storageService.subtitle.set(option);
    reportClosedCaptionState(this._ccEnabled);
  }

  _onAudioChange(option: string) {
    this._Player.setAudioTrack(option);
    AppData!.storageService.audio.set(option);
  }

  _openMenu() {
    this._setState('Interactive');
  }

  _closeMenu() {
    // no-op
  }

  _liveButton() {
    // no-op
  }

  _onControlHover() {
    this._restartIdleTimer();
  }

  private _onBackgrounded() {
    const isPlaying = this._Player.isPlaying();
    this._shouldResumeOnForeground = isPlaying;
    if (isPlaying) {
      this._Player.pause();
    }
  }

  private _onForegrounded() {
    if (this._shouldResumeOnForeground) {
      this._Player.resume();
    }
  }

  protected setupPlayer(useBc: boolean): boolean {
    return true;
  }

  protected exitPlayer() {
    // no-op
  }

  private _initPlayback() {
    this._Player.open();
    this._Player.initialize(this._PlayerEventHandler.getConsumer());
  }

  protected cleanupPlayer() {
    if (this._Player.isClosed) return;

    this._PlayerEventHandler.handlePlayerEnd({
      hasReachedEnd: this._hasReachedEnd,
    });
    this._Player.close();

    const iframes = document.querySelectorAll('iframe[title="Advertisement"]');
    iframes.forEach(iframe => iframe.remove());
  }

  /* =============================
   * AYSW
   * ============================= */

  // We assume this can be called multiple times
  protected async showAyswOrEventEnded() {
    if (!this.pageData) return;

    await this.prepareAyswModal();

    this._Player.pause();

    // check if the modal is already open
    if (!AppData?.modalService.isInHistory('AyswModal')) {
      AppData?.modalService.open('AyswModal');
    }
  }

  protected async prepareAyswModal() {
    // no-op
  }

  protected getAyswLabel(): { showName: string; cancelLabel: string } {
    return { showName: '', cancelLabel: '' };
  }

  protected _handleContinueOnAysw() {
    // no-op
  }

  protected _handleBackOnAysw() {
    // no-op
  }

  protected setupAyswTimer() {
    if (this._ayswTimer === null) this.resetAyswTimer();
  }

  protected resetAyswTimer() {
    this.clearAyswTimeout();
    if (this._ayswIdleLimit > 0) {
      this._ayswTimer = Registry.setTimeout(
        this._ayswTimeoutFunction,
        this._ayswIdleLimit,
      );
    }
  }

  protected setupAysw() {
    if (!this.pageData) return;

    const { showName, cancelLabel } = this.getAyswLabel();

    this.widgets.ayswmodal.update({
      title: translate('playback.stillWatching', showName),
      cancelLabel: cancelLabel,
      onConfirm: this._handleContinueOnAysw.bind(this),
      onCancel: this._handleBackOnAysw.bind(this),
    });
  }

  protected clearAyswTimeout() {
    this._shouldPromptAysw = false;
    if (this._ayswTimer) {
      Registry.clearTimeout(this._ayswTimer);
    }
    this._ayswTimer = null;
  }

  /* =============================
   * TTS
   * ============================= */

  protected _announceCurrentTime(currentTime: number) {
    // no-op
  }

  protected _announcerRefresh() {
    this.fireAncestors(
      '$announcerRefresh',
      getFocusDepth(this.application, this),
    );
  }

  /* =============================
   * CC
   * ============================= */

  private setUpTextTrackDisplay() {
    if (this._Player.videoElement) {
      this._TextTrackDisplay.initializeCaption(
        this._Player.videoElement as HTMLVideoElement,
      );
    }

    AppData?.device.handleCcChange((enabled: boolean) => {
      if (enabled) {
        this._TextTrackDisplay.initializeCaption(
          this._Player.videoElement as HTMLVideoElement,
        );
      } else {
        this._TextTrackDisplay._disable();
      }
    });
  }

  private handleCcChange(tvCcEnabled: boolean) {
    const availableSubtitle = this._Player.getSubtitles()[0];
    if (availableSubtitle) {
      this._onSubtitlesChange(
        tvCcEnabled ? availableSubtitle : constants.closedCaption.none,
      );
    }
  }

  private idleCheck() {
    this.clearIdleCheckTimeout();

    if (!!AppData?.announcerService.getIsSpeaking?.()) {
      this._idleCheckTimeoutId = Registry.setTimeout(
        this.idleCheck.bind(this),
        MILLISECONDS_IN_SECOND,
      );
    } else if (
      this._getState() === 'Interactive' &&
      !this._VideoOverlay.isInMenu()
    ) {
      this._setState('Idle');
    }
  }

  private clearIdleCheckTimeout() {
    if (this._idleCheckTimeoutId) {
      Registry.clearTimeout(this._idleCheckTimeoutId);
      this._idleCheckTimeoutId = null;
    }
  }

  protected navigateAfterExit() {
    // Let history resolve if we did not come from playback
    const fromPageId = this.pageData?.fromPageId ?? PageId.UNKNOWN;
    if (fromPageId !== PageId.PLAYBACK) return Router.back();

    // Let history resolve if content was not a video
    const video = this._video;
    if (!video) return Router.back();

    // Visit the show / movie details page for associated video
    navigateDetailsFromVideo(video);
  }

  protected updateBufferingState() {
    const buffering = this._buffering;
    const paused = this._paused;
    const state = this._getState();
    const isSeeking = this._isCurrentlySeeking;

    if (isSeeking) {
      this.loaded = false;
    } else if (
      !buffering ||
      paused ||
      state === 'EndCard' ||
      state === 'PauseAd'
    ) {
      this.loaded = true;
    } else {
      this.loaded = false;
    }
  }

  static override _states() {
    return [
      class Idle extends this {
        override $enter() {
          this._VideoOverlay.hide();
          this.updateBufferingState();
        }

        override _captureBack() {
          this.exitPlayer();
        }

        override _handleUp() {
          this._setState('Interactive');
        }

        override _handleDown() {
          this._setState('Interactive');
        }

        override _handleLeft() {
          this._setState('Interactive');
        }

        override _handleRight() {
          this._setState('Interactive');
        }
      },
      class Interactive extends this {
        override $enter() {
          this._VideoOverlay.show();
          this._restartIdleTimer();
          this.updateBufferingState();

          this._announcerRefresh();
        }

        override _getFocused() {
          return this._VideoOverlay;
        }

        override _handleBack() {
          this._setState('Idle');
        }

        override _captureKey(event: KeyboardEvent) {
          super._captureKey(event);
          this._restartIdleTimer();
          return false;
        }
      },
    ];
  }
}
